import { useNavigate } from 'react-router-dom'
import { LanguageCode } from 'src/models/language'
import { cn } from 'src/utils'
import type { DocumentMetadata } from 'src/hooks/useDocs'
import Tag from './Tag'
import { Org } from 'src/hooks/useOrg'
import { Session } from 'src/hooks/useSession'

const getOrgName = (orgs: Org[], id: string) => {
  const org = orgs.find((o) => o.id === id)
  return org?.name
}

const Docs: React.FC<{
  docs: DocumentMetadata[]
  fromLang: LanguageCode
  toLang: LanguageCode
  onNavigate?: (path: string) => void
  session?: Session
  orgs?: Org[]
}> = ({
  docs,
  fromLang,
  toLang,
  session,
  onNavigate,
  orgs = [],
}) => {
  const navigate = useNavigate()

  // Move documents with both languages to top
  const sortedDocs = docs?.sort(
    (
      { id: id1, toLangs: tl1 },
      { id: id2, toLangs: tl2 },
    ) => {
      if (session?.documentId === id1) return -1
      if (session?.documentId === id2) return 1
      return (
        +(tl2 ?? []).includes(toLang) -
        +(tl1 ?? []).includes(toLang)
      )
    },
  )

  const handleNavigate = onNavigate ?? navigate

  return (
    <>
      {sortedDocs?.map(
        (
          {
            id,
            title,
            author,
            lang,
            group,
            groupId,
            toLangs,
          },
          index,
        ) => {
          const [toLangId, toVariant] = toLang.split('-')
          const isToLangAvailable =
            toLangs?.includes(toLang)

          return (
            <div
              key={`${id}-${lang}-${index}`}
              onClick={() => handleNavigate(
                  `/reader/${id}?fromLang=${fromLang}&toLang=${toLang}&documentId=${id}`,
                )
              }
            >
              <div className="relative mb-4 block rounded border-b-2 bg-slate-50 px-4 pb-4 pt-4 transition-all ease-in-out hover:cursor-pointer hover:bg-slate-200">
                <h3>{title}</h3>
                <p className="text-slate-500">{author}</p>
                <div className="absolute right-2 mt-2 flex gap-2">
                  {group === 'users' && (
                    <Tag className="black bg-slate-300">
                      My Library
                    </Tag>
                  )}
                  {group === 'orgs' && (
                    <Tag className="black bg-slate-300">
                      {getOrgName(orgs, groupId) ||
                        'My Groups'}
                    </Tag>
                  )}

                  {session?.documentId === id && (
                    <Tag className="bg-[#8fe78a] text-black">
                      Currently Reading
                    </Tag>
                  )}

                  <Tag
                    className={cn(
                      'text-black',
                      !isToLangAvailable
                        ? 'bg-slate-200 text-slate-400'
                        : 'bg-[#8fe78a]',
                    )}
                  >
                    {toLangId}{' '}
                    {toVariant.toLocaleUpperCase()}
                  </Tag>
                </div>
              </div>
            </div>
          )
        },
      )}
    </>
  )
}

export default Docs
